import { setClassName } from '@change-corgi/core/error';

import { PageDataError } from '#src/shared/error';

import type { PsfPromoteOrShareDataQuery } from '../api.graphql';
import type { PsfPromoteOrShareRecruiterQuery } from '../recruiter.graphql';
import type { PsfSocialStarterDataQuery } from '../socialStarter.graphql';

export type PsfPromoteOrSharePageDataErrorCode =
	| 'psf_promote_or_share_page_data_not_found'
	| 'psf_promote_or_share_page_missing_promotion_stats';
export class PsfPromoteOrSharePageDataError extends PageDataError<PsfPromoteOrSharePageDataErrorCode> {}
setClassName(PsfPromoteOrSharePageDataError, 'PsfPromoteOrSharePageDataError');

export type Recruiter = PsfPromoteOrShareRecruiterQuery['recruiter'];
/**
 * EXPERIMENT: psf_featured_spiking_petition_experiment — the Omit, remove with experiment cleanup.
 * Omitted because only the combo query fetches it; api.ts hands it to normalizeData on its own.
 */
export type Petition = Omit<NonNullable<PsfPromoteOrShareDataQuery['petition']>, 'psfFeaturedSpikingPetition'>;
export type Viewer = PsfPromoteOrShareDataQuery['viewer'];
export type PromotionStats = NonNullable<Petition['promotionStats']>;

export type FcmValues = PsfPromoteOrShareDataQuery['featureConfigs'];

export type HeroFeedItem = NonNullable<PsfPromoteOrShareDataQuery['petition']>['feeds']['heroFeed'][0];

/** Not exported: only combo's own experiment sub-types below need it. */
type ComboExperiments = PsfPromoteOrShareDataQuery['experiments'];
export type SocialStarterExperiments = PsfSocialStarterDataQuery['experiments'];
export type PsfShareFirstSocialStarterExperiment = SocialStarterExperiments['psfShareFirstSocialStarterExperiment'];
/** @knip-ignore */
export type PsfComboCopyExperiment = ComboExperiments['psfComboCopyExperiment'];
/**
 * Resolved with `treat: false`: `useTreatFacebookPostSheetExperiment` treats it, and fires `$exposure`,
 * at the Facebook tap (CHANGE-99697).
 * @knip-ignore
 */
export type PsfPromoteOrShareFacebookPostSheetExperiment =
	ComboExperiments['psfPromoteOrShareFacebookPostSheetExperiment'];
// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: false`: `useNativeShareTitle` treats it, and fires `$exposure`, at the native
 * share tap (CHANGE-99262).
 * @knip-ignore
 */
export type NativeShareTitleExperiment = ComboExperiments['nativeShareTitleExperiment'];
// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: false`: `useTreatQrCodeExperiment` treats it, and fires `$exposure`, when the
 * share slide-up opens (CHANGE-99266).
 * @knip-ignore
 */
export type PsfPromoteOrShareQrCodeExperiment = ComboExperiments['psfPromoteOrShareQrCodeExperiment'];
// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
/**
 * Resolved with `treat: false`: `useEnrollConstituentPsfExperiment` treats it, and fires `$exposure`,
 * when the share slide-up opens for an identified local constituent (CHANGE-100768).
 * @knip-ignore
 */
export type ConstituentPsfExperiment = ComboExperiments['constituentPsfExperiment'];
// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
/**
 * Resolved with `treat: false`: `useFeaturedSpikingPetitionExperiment` treats it, and fires `$exposure`,
 * on combo page load for an eligible signer (CHANGE-102055).
 * @knip-ignore
 */
export type PsfFeaturedSpikingPetitionExperiment = ComboExperiments['psfFeaturedSpikingPetitionExperiment'];
/** EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup */
export type FeaturedSpikingPetition = NonNullable<
	NonNullable<PsfPromoteOrShareDataQuery['petition']>['psfFeaturedSpikingPetition']
>;

export type PsfPromoteOrSharePageData = {
	petition: Omit<Petition, 'bandits' | 'headlineBandits' | 'promotionStats' | 'tagsConnection'> & {
		promotionStats: PromotionStats;
		categoryTags: string[];
	};
	recruiter?: Recruiter;
	chipInAmount: number;
	chipInAmountLocalized: string;
	/** Currency `chipInAmount` is denominated in; carried to the share page with it. */
	chipInCurrencyCode: string;
	joinTheCommunityCustomConfig?: {
		promoterCount: number;
		promoterCountLocalized: string;
		contributionAmount: number;
		contributionAmountLocalized: string;
		additionalSignatures: number;
		additionalSignaturesLocalized: string;
	};
	promotionGoal: number;
	promotionGoalLocalized: string;
	hideRecruiterEnabled: boolean;
	isShareSlideUpEnabled: boolean;
	isShareAssetsEnabled: boolean;
	campaignActionsEnabled: boolean;
	whatsappHeadlineExperiment: {
		variation: 'control' | 'variant1' | 'variant2' | 'variant3' | 'variant4' | 'fake_control';
		name: string;
		variants: Partial<
			Readonly<
				Record<
					'variant1' | 'variant2' | 'variant3' | 'variant4' | 'fake_control',
					Readonly<{
						message: string;
					}>
				>
			>
		>;
	};
	signaturesFromShareCountEstimateLocalized: string;
	useDonationLanguage: boolean;
	advocateEntryPointButtonLabel: string;
	advocateEntryPointExperiment: {
		variation: ComboExperiments['advocateEntryPoint']['variation'];
		name: string;
	};
	advocateEntryPointCpDisabled: boolean;
	psfShareFirstSocialStarterExperiment: PsfShareFirstSocialStarterExperiment;
	channelAwareCtaChannels: FcmValues['channelAwareCtaChannels'];
	psfComboCopyExperiment: PsfComboCopyExperiment;
	psfPromoteOrShareFacebookPostSheetExperiment: PsfPromoteOrShareFacebookPostSheetExperiment;
	// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
	nativeShareTitleExperiment: NativeShareTitleExperiment;
	// EXPERIMENT: psf_promote_or_share_qr_code_experiment — remove with experiment cleanup
	psfPromoteOrShareQrCodeExperiment: PsfPromoteOrShareQrCodeExperiment;
	// EXPERIMENT: constituent_psf_experiment (remove with experiment cleanup)
	constituentPsfExperiment: ConstituentPsfExperiment;
	// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
	psfFeaturedSpikingPetitionExperiment: PsfFeaturedSpikingPetitionExperiment;
	// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
	/** `null` when the viewer has no petition to feature; the step and its experiment then stay inert. */
	featuredSpikingPetition: FeaturedSpikingPetition | null;
	/** Kill switch for the Facebook post sheet probe and experiment — see `useFacebookPostSheetEligibility`. */
	facebookPostSheetFcm: PsfPromoteOrShareDataQuery['featureConfigs']['facebookPostSheetFcm'];
	psfComboBanditVariation: 'static' | 'default' | null;
	// No displayable cover image: the petition-media slot is removed entirely.
	hideMediaSlot: boolean;
};
